import type { ItemOption, Menu, MenuItem } from "@/redux/services/menu/menu.type";

/**
 * Option choices for one dish picked inside a combo: item option id → chosen
 * option value ids.
 */
export type ComboOptionChoices = Record<string, string[]>;

/** The cart's stored shape of one combo selection (see `CartEntry`). */
export interface ComboCartSelectionLike {
  menuItem: string;
  selectedOptions?: string[];
}

/**
 * Index the active menu's dishes by placement id. The menu combo read
 * (`menu_combo:read`) embeds a component's dishes without their `itemOptions`,
 * so the POS takes them from the active menu it already loads.
 */
export function indexMenuItems(
  menu: Menu | null | undefined,
): Record<string, MenuItem> {
  const index: Record<string, MenuItem> = {};
  for (const section of menu?.menuSections ?? []) {
    for (const item of section.menuItems ?? []) {
      index[item.id] = item;
    }
  }
  return index;
}

/**
 * The options to offer for a dish picked in a combo: its own `itemOptions`
 * when the payload carries them, else those of the same placement on the
 * active menu. Options whose `menuItemOption` is not embedded (an IRI or an
 * object without a title) cannot be shown and are dropped.
 */
export function comboItemOptions(
  menuItem: Pick<MenuItem, "id"> & { itemOptions?: ItemOption[] },
  menuItemsById: Record<string, MenuItem>,
): ItemOption[] {
  const own = menuItem.itemOptions ?? [];
  const source =
    own.length > 0 ? own : (menuItemsById[menuItem.id]?.itemOptions ?? []);
  return source.filter(
    (io) => typeof io.menuItemOption === "object" && !!io.menuItemOption?.title,
  );
}

/**
 * Toggle one option value. Radio and select keep a single value (tapping the
 * chosen one clears it); checkboxes add or remove, capped at `maxSelections`
 * when it is above 0.
 */
export function toggleComboOptionValue(
  choices: ComboOptionChoices,
  itemOption: Pick<ItemOption, "id" | "maxSelections"> & {
    menuItemOption: { type: string };
  },
  valueId: string,
): ComboOptionChoices {
  const current = choices[itemOption.id] ?? [];
  const type = itemOption.menuItemOption.type;

  if (type === "radio" || type === "select") {
    return {
      ...choices,
      [itemOption.id]: current.includes(valueId) ? [] : [valueId],
    };
  }

  if (current.includes(valueId)) {
    return {
      ...choices,
      [itemOption.id]: current.filter((id) => id !== valueId),
    };
  }
  if (itemOption.maxSelections > 0 && current.length >= itemOption.maxSelections) {
    return choices;
  }
  return { ...choices, [itemOption.id]: [...current, valueId] };
}

/**
 * Whether every required option of the dish has a value. The API rejects a
 * combo selection missing one with `missing_required_option` (SFRESTO-183).
 */
export function comboRequiredOptionsMet(
  itemOptions: Pick<ItemOption, "id" | "isRequired">[],
  choices: ComboOptionChoices | undefined,
): boolean {
  return itemOptions
    .filter((io) => io.isRequired)
    .every((io) => (choices?.[io.id]?.length ?? 0) > 0);
}

/** Chosen value ids in option order, then their titles, for the cart line. */
export function comboChosenOptions(
  itemOptions: ItemOption[],
  choices: ComboOptionChoices | undefined,
): { valueIds: string[]; labels: string[] } {
  const valueIds: string[] = [];
  const labels: string[] = [];
  for (const io of itemOptions) {
    for (const valueId of choices?.[io.id] ?? []) {
      valueIds.push(valueId);
      const title = io.menuItemOption.menuItemOptionValues?.find(
        (v) => v.id === valueId,
      )?.title;
      if (title) labels.push(title);
    }
  }
  return { valueIds, labels };
}

/** "Cheese burger (Double, Sans oignon)" — the dish with its chosen options. */
export function comboSelectionLabel(name: string, optionLabels: string[]): string {
  return optionLabels.length > 0 ? `${name} (${optionLabels.join(", ")})` : name;
}

/**
 * The part of a combo cart entry id that identifies its selections, so the
 * same combo with other dishes or other options becomes its own cart line.
 * Without options it is the sorted dish ids, as before options were sent.
 */
export function comboSelectionKey(selections: ComboCartSelectionLike[]): string {
  return selections
    .map((s) => {
      const options = [...(s.selectedOptions ?? [])].sort();
      return options.length > 0 ? `${s.menuItem}:${options.join("+")}` : s.menuItem;
    })
    .sort()
    .join(",");
}

/**
 * Option value ids from an order read. The API embeds the values as objects;
 * an IRI string is reduced to its id.
 */
export function optionValueIdsFromRead(selectedOptions: unknown[] | undefined): string[] {
  return (selectedOptions ?? [])
    .map((opt) => {
      if (typeof opt === "string") return opt.split("/").pop() ?? "";
      if (opt && typeof opt === "object" && "id" in opt) {
        return String((opt as { id: unknown }).id ?? "");
      }
      return "";
    })
    .filter((id) => id !== "");
}

/** Option value titles from an order read (objects only; IRIs carry none). */
export function optionValueTitlesFromRead(selectedOptions: unknown[] | undefined): string[] {
  return (selectedOptions ?? [])
    .map((opt) =>
      opt && typeof opt === "object" && "title" in opt
        ? String((opt as { title: unknown }).title ?? "")
        : "",
    )
    .filter((title) => title !== "");
}
